<script setup lang="ts">
import type { AddForecastEventInput } from '../internal/types.ts'
import { CurrencyCode, type Money, parseLocalDate } from '@taler/domain'
import { ActionButton, Card, Select, StatusTag, TextField } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'

const props = defineProps<{ isSubmitting: boolean; errorMessage: string | undefined }>()
const emit = defineEmits<{ create: [input: AddForecastEventInput] }>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const currencyOptions = Object.values(CurrencyCode).map((value) => ({ value, label: value }))

const open = ref(false)
const name = ref('')
const amount = ref<Money | null>(null)
const currency = ref<CurrencyCode | undefined>(undefined)
const dateText = ref('')
const validationError = ref<string | undefined>(undefined)

function reset(): void {
  name.value = ''
  amount.value = null
  currency.value = undefined
  dateText.value = ''
  validationError.value = undefined
}

function submit(): void {
  validationError.value = undefined
  if (name.value.trim() === '' || dateText.value === '') return
  if (currency.value === undefined) {
    validationError.value = t('forecast.currencyRequired')
    return
  }
  if (amount.value === null) return
  try {
    const input: AddForecastEventInput = {
      name: name.value.trim(),
      amount: amount.value,
      date: parseLocalDate(dateText.value),
    }
    emit('create', input)
  } catch {
    validationError.value = t('forecast.invalidAmount')
  }
}

watch(
  () => props.isSubmitting,
  (submitting, wasSubmitting) => {
    if (wasSubmitting && !submitting && props.errorMessage === undefined) {
      reset()
      open.value = false
    }
  },
)
</script>

<template>
  <div>
    <ActionButton v-if="!open" type="button" variant="secondary" @click="open = true">
      {{ t('forecast.addEvent') }}
    </ActionButton>

    <Card v-else>
      <form class="flex flex-col items-start gap-3" @submit.prevent="submit">
        <div class="w-80">
          <TextField v-model="name" :label="t('forecast.nameLabel')" maxlength="255" required autofocus />
        </div>

        <div class="flex gap-[var(--space-cell)]">
          <label class="flex flex-col gap-1">
            <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
              t('forecast.amountLabel')
            }}</span>
            <AmountInput
              v-model="amount"
              :currency="currency"
              :locale="currentLocale"
              :placeholder="t('forecast.amountPlaceholder')"
              allow-negative
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
              t('forecast.currencyLabel')
            }}</span>
            <Select
              v-model="currency"
              :options="currencyOptions"
              :placeholder="t('forecast.selectCurrency')"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
              t('forecast.dateLabel')
            }}</span>
            <input v-model="dateText" type="date" required class="field-control" />
          </label>
        </div>

        <StatusTag v-if="validationError ?? errorMessage" tone="danger">
          {{ validationError ?? errorMessage }}
        </StatusTag>

        <div class="flex gap-2">
          <ActionButton type="submit" :disabled="isSubmitting">
            {{ isSubmitting ? t('forecast.saving') : t('forecast.save') }}
          </ActionButton>
          <ActionButton type="button" variant="secondary" @click="open = false">
            {{ t('forecast.cancel') }}
          </ActionButton>
        </div>
      </form>
    </Card>
  </div>
</template>
